import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Sparkles, Target, TrendingUp, AlertTriangle, CheckCircle2, Info, Gauge } from 'lucide-react';
import MatchScoreComponent from './MatchScore';
import JDAnalysisComponent from './JDAnalysis';
import type { MatchScore, JDAnalysis, AtsScoreResponse } from '../types';

interface AIAssistantPanelProps {
  matchScore: MatchScore | null;
  jdAnalysis: JDAnalysis | null;
  tailoringNotes: string[];
  keywordMatches: string[];
  isMatching: boolean;
  jdUrl: string;
  jdText: string;
  useTextMatch: boolean;
  atsScore?: AtsScoreResponse | null;
  isScoringAts?: boolean;
  onJdUrlChange: (v: string) => void;
  onJdTextChange: (v: string) => void;
  onToggleMatchMode: () => void;
  onMatch: () => void;
  onClose?: () => void;
}

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  matchScore,
  jdAnalysis,
  tailoringNotes,
  keywordMatches,
  isMatching,
  jdUrl,
  jdText,
  useTextMatch,
  atsScore,
  isScoringAts,
  onJdUrlChange,
  onJdTextChange,
  onToggleMatchMode,
  onMatch,
  onClose,
}) => {
  const [showInput, setShowInput] = useState(!matchScore);

  return (
    <div>
      {onClose && (
        <div className="flex items-center justify-between px-4 pt-4 pb-2 lg:hidden">
          <span className="text-xs font-semibold text-zinc-400 tracking-wider uppercase">AI Assistant</span>
          <button
            className="w-7 h-7 flex items-center justify-center rounded-btn text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="px-4 pb-4 space-y-5">
        {/* ATS Score Checklist */}
        {atsScore && (
          <div className="pt-2">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-semibold text-emerald-600 tracking-wider uppercase">ATS Optimization Checklist</span>
              <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-white bg-slate-900 px-2 py-0.5 rounded-full">
                {isScoringAts ? '...' : `${atsScore.score}/100`}
              </span>
            </div>
            {atsScore.action_items.length === 0 ? (
              <div className="text-xs text-emerald-600 p-3 bg-emerald-50 border border-emerald-200 rounded-btn flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Great job — no actionable improvements needed.</span>
              </div>
            ) : (
              <ul className="space-y-2">
                {atsScore.action_items.map((item) => {
                  const styles =
                    item.severity === 'critical'
                      ? 'bg-red-50 border-red-200 text-red-700 border-l-red-500'
                      : item.severity === 'warning'
                        ? 'bg-amber-50 border-amber-200 text-amber-700 border-l-amber-500'
                        : 'bg-sky-50 border-sky-200 text-sky-700 border-l-sky-500';
                  const Icon =
                    item.severity === 'critical' || item.severity === 'warning'
                      ? AlertTriangle
                      : Info;
                  const iconColor =
                    item.severity === 'critical'
                      ? 'text-red-500'
                      : item.severity === 'warning'
                        ? 'text-amber-500'
                        : 'text-sky-500';
                  return (
                    <li key={item.id} className={`text-xs p-2.5 px-3 border border-l-3 rounded-btn leading-relaxed flex items-start gap-2 ${styles}`}>
                      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${iconColor}`} />
                      <span>{item.text}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Match Input */}
        {!matchScore && (
          <div className="pt-2">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-brand-500" />
              <span className="text-xs font-semibold text-brand-500 tracking-wider uppercase">Match to Job</span>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-xs font-medium cursor-pointer select-none transition-colors ${!useTextMatch ? 'text-foreground font-semibold' : 'text-zinc-400'}`} onClick={onToggleMatchMode}>URL</span>
              <label className="relative w-9 h-4.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useTextMatch}
                  onChange={onToggleMatchMode}
                  className="opacity-0 w-0 h-0 absolute"
                />
                <span className={`absolute inset-0 rounded-full transition-colors before:content-[''] before:absolute before:w-3.5 before:h-3.5 before:left-[2px] before:bottom-[2px] before:bg-white before:rounded-full before:transition-transform ${useTextMatch ? 'bg-brand-500 before:translate-x-[14px]' : 'bg-zinc-300'}`} />
              </label>
              <span className={`text-xs font-medium cursor-pointer select-none transition-colors ${useTextMatch ? 'text-foreground font-semibold' : 'text-zinc-400'}`} onClick={onToggleMatchMode}>Text</span>
            </div>
            {!useTextMatch ? (
              <input
                type="url"
                className="w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-400 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100 mb-2"
                placeholder="Paste job posting URL..."
                value={jdUrl}
                onChange={(e) => onJdUrlChange(e.target.value)}
              />
            ) : (
              <textarea
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-400 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100 min-h-[100px] resize-y mb-2"
                placeholder="Paste job description text..."
                value={jdText}
                onChange={(e) => onJdTextChange(e.target.value)}
                rows={4}
              />
            )}
            <button
              className="w-full h-10 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-none rounded-btn text-sm font-semibold cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_15px_rgba(16,185,129,0.4)] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              onClick={onMatch}
              disabled={(!useTextMatch && !jdUrl.trim()) || (useTextMatch && !jdText.trim()) || isMatching}
            >
              <Search className="w-4 h-4" />
              {isMatching ? 'Analyzing...' : 'Match & Analyze'}
            </button>
          </div>
        )}

        {/* Match Results */}
        <AnimatePresence mode="wait">
          {matchScore && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-5"
            >
              {showInput && (
                <button
                  className="w-full h-9 px-3 border border-zinc-200 text-zinc-500 hover:text-foreground hover:bg-zinc-50 text-xs font-medium rounded-btn transition-colors inline-flex items-center justify-center gap-1.5"
                  onClick={() => { setShowInput(false); onJdUrlChange(''); onJdTextChange(''); }}
                >
                  <Search className="w-3.5 h-3.5" />
                  Match another job
                </button>
              )}

              <MatchScoreComponent score={matchScore} />

              {jdAnalysis && (
                <div className="border-t border-zinc-100 pt-5">
                  <JDAnalysisComponent
                    analysis={jdAnalysis}
                    keywordMatches={keywordMatches}
                  />
                </div>
              )}

              {tailoringNotes.length > 0 && (
                <div className="border-t border-zinc-100 pt-5">
                  <h3 className="text-xs font-semibold text-zinc-400 tracking-wider uppercase mb-3 flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
                    Tailoring Notes
                  </h3>
                  <ul className="space-y-2">
                    {tailoringNotes.map((note, i) => (
                      <li key={i} className="text-xs text-zinc-600 p-2.5 px-3 bg-amber-50 border border-amber-200 rounded-btn leading-relaxed flex items-start gap-2">
                        <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t border-zinc-100 pt-5">
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                  <span>AI suggestions available below</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AIAssistantPanel;
